import { forwardRef, InputHTMLAttributes, useCallback } from 'react';
import clsx from 'clsx';
import { formatPhoneNumber } from '@/lib/phone';

interface PhoneNumberInputProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'onChange' | 'value'> {
  value: string;
  onChangeText: (value: string) => void;
  label?: string;
  error?: string;
}

export const PhoneNumberInput = forwardRef<
  HTMLInputElement,
  PhoneNumberInputProps
>(({ value, onChangeText, label, error, className, ...props }, ref) => {
  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const formatted = formatPhoneNumber(e.target.value);
      onChangeText(formatted);
    },
    [onChangeText]
  );

  return (
    <div className='w-full'>
      {label && (
        <label className='block text-sm text-gray-500 mb-2'>{label}</label>
      )}
      <div className='relative'>
        <span className='absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 select-none'>
          +1
        </span>
        <input
          ref={ref}
          type='tel'
          value={value}
          onChange={handleChange}
          placeholder='(555) 123-4567'
          className={clsx(
            'w-full pl-12 pr-4 py-3 rounded-xl',
            'bg-gray-50/90 border border-gray-900/8',
            'text-gray-900 placeholder-gray-400',
            'focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary',
            'transition-all duration-200',
            'font-sans text-base',
            error && 'border-red-500 focus:ring-red-200',
            className
          )}
          {...props}
        />
      </div>
      {error && <p className='mt-1 text-sm text-red-500'>{error}</p>}
    </div>
  );
});

PhoneNumberInput.displayName = 'PhoneNumberInput';
